const formatDate = (value, locale = 'en-KE') => value ? new Date(value).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' }) : 'To be communicated';

const addWrapped = (pdf, text, x, y, width, lineHeight = 5) => {
  const lines = pdf.splitTextToSize(text, width);
  pdf.text(lines, x, y);
  return y + (lines.length * lineHeight);
};

const loadLogo = async url => {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (_) { return null; }
};

export const generateAdmissionLetter = async application => {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const institution = application.institution_name || 'Institution';
  const office = application.admissions_office_name || 'OFFICE OF THE REGISTRAR (ACADEMIC AFFAIRS)';
  const fullName = (application.applicant_name || '').toUpperCase();
  const locale = application.institution_locale || 'en-KE';
  const reportingDate = formatDate(application.reporting_date, locale);
  const logo = await loadLogo(application.institution_logo_url);
  const signature = await loadLogo(application.registrar_signature_url);

  if (logo) { try { pdf.addImage(logo, undefined, 89, 7, 32, 28, undefined, 'FAST'); } catch (_) {} }
  pdf.setTextColor(185, 28, 28); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
  pdf.text(application.application_number, 194, 14, { align: 'right' });
  pdf.setTextColor(0, 0, 0); pdf.setFontSize(18);
  pdf.text(institution.toUpperCase(), 105, logo ? 41 : 20, { align: 'center' });
  pdf.setFontSize(11); pdf.text(office.toUpperCase(), 105, logo ? 47 : 27, { align: 'center' });
  if (application.institution_motto) { pdf.setFont('helvetica', 'italic'); pdf.setFontSize(8.5); pdf.text(application.institution_motto, 105, logo ? 52 : 32, { align: 'center' }); }

  const headerY = logo ? 56 : 37;
  pdf.setLineWidth(0.7); pdf.line(15, headerY, 195, headerY);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
  pdf.text(`Tel: ${application.admissions_phone || application.institution_phone || 'Not configured'}`, 16, headerY + 6);
  pdf.text(`Address: ${application.institution_postal_address || application.institution_address || 'Not configured'}`, 194, headerY + 6, { align: 'right', maxWidth: 90 });
  pdf.text(`Website: ${application.institution_website || 'Not configured'}`, 16, headerY + 11);
  pdf.text(`Email: ${application.admissions_email || application.institution_email || 'Not configured'}`, 194, headerY + 11, { align: 'right' });
  pdf.text(`Our Ref: ${application.letter_reference || application.application_number}`, 16, headerY + 16);
  pdf.text(`Date: ${formatDate(application.offered_at || new Date(), locale)}`, 194, headerY + 16, { align: 'right' });
  pdf.setLineWidth(0.8); pdf.line(15, headerY + 19, 195, headerY + 19);

  let y = headerY + 27;
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10.5); pdf.text(fullName, 16, y); y += 6;
  pdf.setFont('helvetica', 'normal'); pdf.text(`Dear ${application.applicant_name?.split(' ')[0] || 'Applicant'},`, 16, y); y += 8;
  const subject = `RE: ADMISSION TO ${institution.toUpperCase()}`;
  pdf.setFont('helvetica', 'bold'); pdf.text(subject, 16, y); pdf.line(16, y + 1, Math.min(194, 16 + pdf.getTextWidth(subject)), y + 1); y += 7;
  pdf.setFont('helvetica', 'normal');
  y = addWrapped(pdf, `We are pleased to offer you admission to pursue ${application.program_name}, Level ${application.applied_level}, under the ${application.intake_name} intake for academic year ${application.academic_year}. This offer is subject to verification of the original academic and identification documents submitted during application.`, 16, y, 178); y += 4;
  pdf.setFont('helvetica', 'bold'); pdf.text(`Your Admission / Registration Number is ${application.registration_number || 'Pending allocation'}`, 16, y); y += 9;
  pdf.setFont('helvetica', 'normal');
  y = addWrapped(pdf, `Congratulations on this achievement. Registration is subject to the institution's regulations and the following conditions:`, 16, y, 178); y += 2;
  y = addWrapped(pdf, 'i. Present this letter together with your original academic documents and identification when reporting.', 21, y, 170);
  y = addWrapped(pdf, 'ii. Complete registration and pay all applicable fees within the communicated timelines.', 21, y, 170);
  y = addWrapped(pdf, "iii. Observe the institution's academic, conduct and responsible data-use regulations.", 21, y, 170); y += 5;
  y = addWrapped(pdf, `The programme is expected to commence on ${reportingDate}. You are required to report physically to the institution on that date to complete verification and registration.`, 16, y, 178); y += 3;
  if (application.student_email) { y = addWrapped(pdf, `Your institutional student email is ${application.student_email}. Account activation and initial-access instructions will be issued securely; no permanent password is printed on this letter.`, 16, y, 178); y += 3; }
  y = addWrapped(pdf, 'Official communication will be sent through the institution website and your institutional student email. Keep your personal application email available as a recovery contact.', 16, y, 178); y += 5;
  pdf.text(`We look forward to welcoming you to ${institution}.`, 16, y); y += 7; pdf.text('Yours sincerely,', 16, y); y += 3;
  if (signature) { try { pdf.addImage(signature, undefined, 16, y, 30, 12, undefined, 'FAST'); } catch (_) {} }
  y += 14;
  pdf.setFont('helvetica', 'bold'); pdf.text((application.registrar_name || 'REGISTRAR').toUpperCase(), 16, y); y += 5;
  pdf.text((application.registrar_title || 'REGISTRAR - ACADEMIC AFFAIRS').toUpperCase(), 16, y);
  pdf.setLineWidth(0.8); pdf.line(15, 280, 195, 280); pdf.setFont('helvetica', 'italic'); pdf.setFontSize(9);
  pdf.text(application.admission_letter_footer || application.institution_motto || 'Official admission letter generated by the admissions portal', 105, 287, { align: 'center' });
  pdf.save(`Admission-Letter-${application.application_number}.pdf`);
};
